import React from 'react';

function formatCellValue(value, type) {
  if (value === null || value === undefined) return '\u2014';
  if (type === 'date') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
  }
  if (type === 'number') {
    const n = parseFloat(value);
    if (isNaN(n)) return String(value);
    return n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
}

export default function ResultsTable({
  results,
  loading,
  error,
  orderBy,
  onSort,
  page,
  pageSize,
  onPageChange,
}) {
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-gray-600">
        <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12h-7.5c-.621 0-1.125.504-1.125 1.125M12 12h7.5c.621 0 1.125.504 1.125 1.125" />
        </svg>
        <p className="text-sm">Select a report type and click Run</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {results.columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => onSort(col.key)}
                  className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {orderBy?.column === col.key && (
                      <span className="text-primary-600">
                        {orderBy.direction === 'ASC' ? '\u2191' : '\u2193'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {results.rows.length === 0 ? (
              <tr>
                <td colSpan={results.columns.length} className="px-4 py-8 text-center text-sm text-gray-600">
                  No results found
                </td>
              </tr>
            ) : (
              results.rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {results.columns.map(col => (
                    <td key={col.key} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {formatCellValue(row[col.key], col.type)}
                    </td>
                  ))}
                </tr>
              ))
            )}

            {/* Grand totals row */}
            {results.grandTotals && (
              <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                {results.columns.map((col, idx) => (
                  <td key={col.key} className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-900">
                    {idx === 0
                      ? 'Grand Total'
                      : results.grandTotals[col.key] !== undefined
                        ? formatCellValue(results.grandTotals[col.key], 'number')
                        : ''
                    }
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-2.5 border-t border-gray-200 flex items-center justify-between bg-gray-50">
        <span className="text-xs text-gray-600">
          {results.totalRows === 0
            ? 'No results'
            : `${results.offset + 1}\u2013${Math.min(results.offset + results.rows.length, results.totalRows)} of ${results.totalRows.toLocaleString()}`
          }
        </span>
        <div className="flex gap-1.5">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={results.offset + results.rows.length >= results.totalRows}
            className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
